import { Box } from '@mui/material';
import { useStore } from '@xyflow/react';

import { type TileNodeProps } from '@/components/diagrams/FlowChart/nodes/TileNode/TileNode';
import { COLORS } from '@/styles/tokens/colors';

import { useDiagramTextFields } from '../../hooks/useDiagramTextFields';
import { type ReactFlowDiagramNodeData } from '../../utils/reactFlowDiagram';
import { DiagramNodeFrame } from './DiagramNodeFrame';
import { TEXT_FIELDS } from './DiagramTextField';

/**
 * The space around the fields of a text node, in theme spacing units. It
 * matches the space of a tile. The left space also includes the width of
 * the 6px color bar of a tile.
 */
const TEXT_CARD_PADDING = { py: 1.5, pl: 2.75, pr: 2 };

/**
 * A TEXT node of the builder: a white card with a light border that shows
 * the Markdown notes of the node, as the "Advisor notes" card of the diagram
 * builder handoff. A double-click edits the notes in the rich-text editor.
 * It wraps its text at its width.
 *
 * Older TEXT nodes can also have a title, a subtitle, or a footer. The card
 * shows each of these fields that has text, so no saved text disappears, and
 * a double-click on such a field edits it. A field without text does not
 * show, even while the user edits the notes.
 *
 * The node has no set height, so its measured size always includes all of
 * its text. The saved height is the smallest card height. While the user
 * resizes the note, React Flow sets the node height, and the card follows it.
 */
export function DiagramTextNode(props: TileNodeProps) {
  const { id, data } = props;
  const { item } = data as ReactFlowDiagramNodeData;
  const fields = TEXT_FIELDS.filter(
    (field) => field === 'detail' || !!item[field]
  );
  const { renderField, onDoubleClick } = useDiagramTextFields(props, fields);
  const resizeHeight = useStore((state) => state.nodeLookup.get(id)?.height);
  return (
    <DiagramNodeFrame {...props}>
      <Box
        onDoubleClick={onDoubleClick}
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          minHeight: resizeHeight ?? item.nodeStyle?.height ?? undefined,
          ...TEXT_CARD_PADDING,
          bgcolor: COLORS.PRIMITIVES.WHITE,
          border: `1px solid ${COLORS.GRAY[300]}`,
          borderRadius: 1,
        }}
      >
        {renderField('label')}
        {renderField('subtitle')}
        {renderField('detail')}
        {renderField('footerLabel')}
      </Box>
    </DiagramNodeFrame>
  );
}
